<template>
<div class="share_form" :class="{'px-3': !new_share}">
    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    
    <!-- Step 1: Share Form -->
    <template v-if="!new_share">

        <!-- List of entities that are shared -->
        <div class="row">
            <shared-entities-list :entities="shared_entities" />
        </div>

        <div v-if="is_folder_share" class="mt-2 alert alert-info">
            <i class="ion-information-circled mr-2"/>
            Folder shares include all subfolders and files, even ones added later. To share only specific files, select them and create a multi-file share.
        </div>

        <form @submit.prevent="submit_share">
            <div class="mt-4 d-flex flex-column gap-2">
                <div>
                    <CustomCheckbox :disabled="password_protection_mandatory" v-model="enable_password_protection">
                        Password Protection
                        <span v-if="password_protection_mandatory" class="text-sm text-muted ml-1" v-popover="'Your team settings require password protection of all shares. Team Admins can change this setting.'">(Team enforced)</span>
                    </CustomCheckbox>
                </div>
                
                <div>
                    <CustomCheckbox :disabled="expiration_mandatory" v-model="enable_expiration">
                        Expiry Date
                        <span v-if="expiration_mandatory" class="text-sm text-muted ml-1" v-popover="'Your subscription does not allow shares that never expire.'">(Team enforced)</span>
                    </CustomCheckbox>
                    <div v-if="enable_expiration" class="ml-2 mb-2 d-flex align-items-center justify-content-start gap-2 text-nowrap">
                        <span>In </span>
                        <input v-if="!editShare" type="number" v-model.number="expire_days" :step="1" :min="1" :max="share_max_lifetime_days" :oninvalid="`Maximum ${share_max_lifetime_days} days are allowed`" class="form-control form-control-sm text-center" style="max-width: 60px;" />
                        <span v-else>{{ expire_days }}</span><span> day{{ expire_days == 1 ? '' : 's' }}, on</span>
                        <input type="datetime-local" v-model="expire_local_datetime" :min="current_datetime" class="form-control form-control-sm d-inline-block" />
                        <LocalTimezoneBadge />
                    </div>
                    
                </div>

                <div v-if="!is_folder_share">
                    <CustomCheckbox description="Download Limit" v-model="enable_download_limit" />
                    <div v-if="enable_download_limit" class="ml-2 mb-2 d-flex align-items-center justify-content-start gap-2">
                        Maximum <input type="number" v-model.number="downloads_per_file" :min="1" :step="1" class="form-control form-control-sm text-center" style="max-width: 60px;" />
                        download{{ downloads_per_file == 1 ? '' : 's' }}<template v-if="shared_entities.length > 1"> per file</template>
                    </div>
                    <div v-if="enable_download_limit && download_limit_reached_file_names.length" class="ml-2 alert alert-warning">
                        <i class="ion-alert-circled mr-2"/>{{ download_limit_reached_file_names.length == 1 ? download_limit_reached_file_names[0] : `${download_limit_reached_file_names.length} of ${shared_entities.length} files` }} already reached this limit and won't be downloadable any more
                    </div>
                </div>

                <div>
                    <CustomCheckbox v-model="enable_message" description="Custom Note"/>
                    <div v-if="enable_message" class="ml-2 mb-2">
                        <textarea class="form-control" v-model="message" :placeholder="`Shown above the shared ${shared_things_str}`" :maxlength="max_message_length"></textarea>
                        <div class="d-flex justify-content-end" v-if="message.length > max_message_length/4"><small class="text-right">{{ message.length }} / {{ max_message_length }}</small></div>
                    </div>
                </div>

                <div class="mt-4 d-flex justify-content-center">
                    <button class="btn btn-secondary mr-2" :class="{'btn-sm text-sm': is_mobile}" type="button" @click="$emit('close')" aria-label="Close">Cancel</button>
                    <button class="btn btn-info btn-gradient" :class="{'btn-sm text-sm': is_mobile}" type="submit" :disabled="loading || disable_submit">
                        <template v-if="is_update">Update Share</template>
                        <template v-else>Get Link{{ enable_password_protection ? ' and Password' : '' }}</template>
                        <i v-if="loading" class="ion-load-c spin ml-2"></i>
                    </button>
                </div>
            </div>
        </form>
        <template v-if="false">
            <pre>{{ payload }}</pre>
            <hr/>
            <pre>{{ editShare }}</pre>
        </template>
    </template>

    <!-- Step 2: display new share link & pass -->
    <template v-else-if="new_share">
        <div>
            <label for="new_share_link">Share Link</label>
            <div class="input-group mt-0">
                <input type="text" class="form-control" id="new_share_link" :value="new_share.link">
                <button class="btn btn-gradient btn-secondary" :class="{'btn-sm text-sm': is_mobile}" @click="to_clipboard(new_share.link)"><i class="ion-clipboard icon-lg"></i><span class="d-none d-md-inline-block ml-1">Copy</span></button>
                <a class="btn btn-primary ml-1" :class="{'btn-sm text-sm': is_mobile}" :href="new_share.link" target="_BLANK"><i class="ion-share icon-lg mr-1"></i> Open</a>
            </div>
        </div>
        <div class="mt-3" v-if="new_share.password_protected">
            <label for="new_share_pass">Password</label>
            <div class="input-group" style="width: 300px">
                <input type="text" class="form-control" id="new_share_pass" :value="new_share.password">
                <button class="btn btn-gradient btn-secondary" :class="{'btn-sm text-sm': is_mobile}" @click="to_clipboard(new_share.password)"><i class="ion-clipboard icon-lg"></i><span class="d-none d-md-inline-block ml-1">Copy</span></button>
            </div>
        </div>

        <div class="text-center mt-3">
            <button class="btn btn-outline btn-secondary" :class="{'btn-sm text-sm': is_mobile}" @click="$emit('close')">Close</button>
        </div>
    </template>
</div>
</template>
<script>

import { ShareService } from '@/services/share-service.js'
import { Utils } from '@/helpers/utils.js'
import { NamespaceService } from '@/services/namespace-service';
import sharedEntitiesList from './shared-entities-list.vue';
import CustomCheckbox from './custom-checkbox.vue';
import LocalTimezoneBadge from './local-timezone-badge.vue';
import { bus, events } from '@/helpers/event_bus';
const DEFAULT_EXPIRATION_DAYS = 7
const MAX_MESSAGE_LENGTH = 1000 // characters

export default {
    props: {
        // Team settings of the currently logged in user (may be null before its loaded)
        teamSettings: Object, 
        
        // File(s) or folder to include in a new share (passed in new share mode)
        entities: { 
            type: [Array, null],
            default: () => null
        },

        // The existing share that is being edited (passed in share edit mode)
        editShare: { 
            type: [Object, null],
            default: () => null
        }
    },

    components: {
        sharedEntitiesList,
        CustomCheckbox,
        LocalTimezoneBadge
    },

    data(){
        return {
            error: false,
            loading: false,
            disable_submit: false,

            enable_password_protection: true,
            enable_expiration: false,

            expire_local_datetime: '',
            expire_days: 0,
            expire_syncing: false, // Avoid the two watchers triggering each other infinitely
            current_datetime: null, // To set the minimum date

            enable_download_limit: false,
            downloads_per_file: 1,

            enable_message: false,
            message: "",
            max_message_length: MAX_MESSAGE_LENGTH,

            new_share: null,
            NamespaceService: NamespaceService,
            to_clipboard: Utils.to_clipboard,
            get_timezone: Utils.get_local_timezone
        }
    },

    computed: {

        is_update(){ // True when an existing share is edited, False if a new Share is created
            return !!this.editShare
        },

        shared_entities(){
            return this.editShare ? this.editShare.entities : this.entities
        },

        is_folder_share(){
            return this.shared_entities[0].entity_type === NamespaceService.ENTITY_TYPE_FOLDER
        },

        shared_things_str(){
            return this.is_folder_share ? `folder` : `file${this.shared_entities.length == 1 ? '' : 's'}`
        },

        share_max_size_bytes(){
            var limit = this.teamSettings?.config.share_max_size
            return (!limit || limit == 0) ? false : limit * 1024*1024*1024
        },
        share_max_files(){
            var limit = this.teamSettings?.config.share_max_files
            return (!limit || limit == 0) ? false : limit
        },
        share_max_lifetime_days(){
            // Max lifetime of a share, in days. If the team config does not define a limit, it's set to 365 days
            var limit = this.teamSettings?.config.share_expire_days_to
            return (!limit || limit == 0) ? false : limit
        },
        expiration_mandatory(){
            return this.share_max_lifetime_days != false
        },
        share_default_lifetime_days(){
            return this.share_max_lifetime_days ? Math.min(this.share_max_lifetime_days, DEFAULT_EXPIRATION_DAYS) : DEFAULT_EXPIRATION_DAYS
        },
        
        password_protection_mandatory(){
            return this.teamSettings?.is_passwordless_share_allowed == false
        },

        download_limit_reached_file_names(){
            // If a share is being edited and the new form download limit has been reached by some files already, 
            // this property return the names of files that will not be downloadable any more
            if(this.is_update && !this.is_folder_share){
                return this.editShare.entities.filter(e => e.downloads_count >= this.downloads_per_file).map(f => f.name)
            }
            return []
        },

        is_mobile(){
            return Utils.is_mobile()
        },

        
    },

    watch: {

        editShare(){
            // Re-init when a different share is displayed
            this.init()
        },

        // Update `expire_local_datetime` when `expire_days` changes
        expire_days(new_expire_days){
            if(this.expire_syncing) return
            this.expire_syncing = true

            // Set expire_datetime
            this.expire_local_datetime = this.get_current_local_datetime(null, new_expire_days)
            this.$nextTick(() => {
                this.expire_syncing = false
            });
        },

        // Update `expire_days` when `expire_local_datetime` changes
        expire_local_datetime(new_expire_datetime) {
            if(this.expire_syncing) return
            this.expire_syncing = true

            const inputDate = new Date(new_expire_datetime);
            const now = new Date();

            // Difference in milliseconds
            const diffMs = inputDate - now;

            // Convert to days (1 day = 86400000 ms)
            const diffDays = diffMs / (1000 * 60 * 60 * 24);

            this.expire_days = Math.ceil(diffDays)

            this.$nextTick(() => {
                this.expire_syncing = false
            });
        },

        expiration_mandatory(newval){
            // If team settings are loaded after the form is displayed, `enable_expiration` should be updated
            if(newval){
                this.enable_expiration = true
            }
        },

        password_protection_mandatory(newval){
            // If team settings are loaded after the form is displayed, `enable_password_protection` should be updated
            if(newval) 
                this.enable_password_protection = true
        },

        enable_expiration(newval, oldval){
            if(!oldval && newval){
                // Expiration turned on
                this.expire_days = this.share_default_lifetime_days
            }
        }

    },
    
    mounted() {
        // Refresh team settings
        bus.$emit(events.RELOAD_TEAM_SETTINGS)
        
        this.init()
    },

    methods: {

        init(){
            // Init 
            this.error = false
            this.disable_submit = true
            this.loading = false

            /* Validate share */

            // Cannot be empty
            if(this.shared_entities.length == 0){
                this.error = "Empty share."
                return
            }

            // Make sure entities are not mixed files & folders
            if (!this.shared_entities.every(en => en.entity_type === this.shared_entities[0].entity_type)) {
                this.error = "Cannot share files and folders mixed!"
                return
            }

            // Folder share cannot contain anything else than a single folder
            if(this.is_folder_share && this.shared_entities.length > 1){
                this.error = "Folder Shares cannot contain more than a single folder!"
                return
            }

            // Check number of files
            if(this.share_max_files && !this.is_folder_share && this.shared_entities.length > this.share_max_files){
                this.error = `Maximum ${this.share_max_files} files are allowed in a single share. Upgrade your Team account to enable larger shares!`
                return
            }

            // Check file share max size
            if(!this.is_folder_share && this.share_max_size_bytes){
                const total_size = this.shared_entities.reduce((sum, en) => sum + (en.size || 0), 0);
                if(total_size > this.share_max_size_bytes){
                    this.error = `Maximum ${Utils.format_bytes(this.share_max_size_bytes)} allowed in a single share. Upgrade your Team account to enable larger shares!`
                    return
                }
            }

            // OK
            this.disable_submit = false
            this.error = false
            this.new_share = null
            this.expire_local_datetime = ''
            this.expire_days = 0
            this.expire_syncing = false
            this.current_datetime = null
            this.enable_download_limit = false
            this.downloads_per_file = 1
            this.enable_message = false
            this.message = ""
            this.new_share = null

            if(this.editShare){
                const share = this.editShare
                
                // Reset form switches, they will be enabled based on the edited share 
                this.enable_expiration = false
                this.enable_download_limit = false
                this.enable_message = false

                // Password protection might have became mandatory since the passwordless share was created
                this.enable_password_protection = share.password_protected || this.password_protection_mandatory 

                if(share.expire) {
                    this.enable_expiration = true
                    this.$nextTick(() => { 
                        // Set the expiration date on the next tick, so `enable_expiration = true` 
                        // does not overwrite the number of days with the default
                        this.expire_local_datetime = this.get_current_local_datetime(new Date(share.expire * 1000))
                    })
                }
                else if(this.expiration_mandatory){
                    // Expiration might have became mandatory since the non-expiring share was created
                    this.enable_expiration = true
                    this.expire_days = this.share_default_lifetime_days
                }
                if(share.message && share.message.length){
                    this.enable_message = true
                    this.message = share.message
                }

                if(share.download_limit){
                    this.enable_download_limit = true
                    this.downloads_per_file = share.download_limit
                }
            }
            else{
                this.enable_password_protection = (this.password_protection_mandatory == true)
                this.enable_expiration = (this.expiration_mandatory == true)
                this.expire_days = this.share_default_lifetime_days // This updates `this.expire_datetime` as well
            }
            this.current_datetime = this.get_current_local_datetime() // Set the min expiration datetime to now
        },

        // Constructs the payload to send 
        _make_payload(){ 
            const payload = {
                password_protected: this.password_protection_mandatory || this.enable_password_protection,
            }
            if(this.editShare){
                payload.id = this.editShare.id
            }
            if(this.is_folder_share){
                payload.type = ShareService.SHARE_TYPE_FOLDER
                payload.folder = this.shared_entities[0].id
            }
            else{
                payload.type = ShareService.SHARE_TYPE_FILE
                payload.files = this.shared_entities.map(e => e.id)
            }
            if(this.enable_download_limit){
                payload.download_limit = this.downloads_per_file
            }
            
            if(this.enable_message && this.message.length) payload.message = this.message
            if(this.enable_expiration){
                // Convert local datetime to UTC
                try {
                    payload.expire_datetime_utc = (new Date(this.expire_local_datetime)).toISOString()
                } catch (error) {
                    // The datetime string is not a valid date (e.g., the user manually cleared the input), use expire_days instead
                    payload.expire_days = this.expire_days
                }
            }
            return payload
        },

        async submit_share(){
            let payload = null
            try {
                payload = this._make_payload()
            } catch (error) {
                this.error = "Share invalid"
                this.disable_submit = true
            }

            if(this.disable_submit) return
            
            this.error = false
            this.loading = true

            if(this.is_update){
                const updated_share = await ShareService.update_share(payload)
                    .then(res => res.body)
                    .catch(err => {
                        this.error = err.body?.message ?? "Internal error"
                        return null
                    })
                    .finally(() => this.loading = false)

                    if(!updated_share) return
                    Utils.show_success("Share updated")
                    bus.$emit(events.SHARE_UPDATED, updated_share)
                    // Close the modal
                    this.$emit('close')
            }
            else{
                const new_share = await ShareService.create_share(payload)
                    .then(res => res.body)
                    .catch(err => {
                        this.error = err.body?.message ?? "Internal error"
                        return null
                    })
                    .finally(() => this.loading = false)
    
                if(!new_share) return
    
                new_share.link = this.$options.filters['share_link'](new_share.alphakey)
                this.new_share = new_share
                
                // Notify the file list about what was shared, so it can increase the share counter.
                bus.$emit(events.ENTITIES_SHARED, new_share.contents)
                // Do not close the modal automatically, the user might want to copy the link/password
            }
        },

        get_current_local_datetime(date=null, daysOffset = 0) {
            const now = date ? date : new Date();
            if(daysOffset > 0) now.setDate(now.getDate() + daysOffset);
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');

            return `${year}-${month}-${day}T${hours}:${minutes}`;
        },
    }
}

</script>
<style lang="css" scoped>

.entity_name{
    white-space: nowrap;        /* Prevents the text from wrapping */
    overflow: hidden;           /* Hides any overflow text */
    text-overflow: ellipsis;    /* Adds the "..." at the end */
    max-width: 400px;           /* Set a max width to control the cutoff point */
}
</style>